import type { Hand, HandId, SceneShape, ShapeRole } from './model.js';

export const HANDS: Record<HandId, Hand> = {
  'kiln-exact': {
    id: 'kiln-exact',
    name: 'Kiln exact',
    description: 'Warm technical illustration: exact structure, quiet fills, one taught element.',
    background: '#FDFCF8',
    plate: '#ECEBE6',
    surface: 'plate',
    exact: true,
  },
  'technical-draft': {
    id: 'technical-draft',
    name: 'Technical draft',
    description: 'Pure line, measured edges and restrained hatching. No decorative mass.',
    background: '#FDFCF8',
    plate: 'transparent',
    surface: 'transparent',
    exact: true,
  },
  'teacher-sketch': {
    id: 'teacher-sketch',
    name: 'Teacher sketch',
    description: 'The same exact anchors with a low-amplitude, permanently seeded human stroke.',
    background: '#FDFCF8',
    plate: 'transparent',
    surface: 'transparent',
    exact: false,
  },
  'soft-geometry': {
    id: 'soft-geometry',
    name: 'Soft geometry',
    description: 'Rounded conceptual modules with quiet violet, blue and amber semantic fills.',
    background: '#FDFCF8',
    plate: 'transparent',
    surface: 'transparent',
    exact: true,
  },
  'editorial-contrast': {
    id: 'editorial-contrast',
    name: 'Editorial contrast',
    description: 'Bold silhouettes and strict colour hierarchy for comparisons and central arguments.',
    background: '#FDFCF8',
    plate: 'transparent',
    surface: 'transparent',
    exact: true,
  },
  'living-line': {
    id: 'living-line',
    name: 'Living line',
    description: 'Organic contours with biological greens and purple causal marks.',
    background: '#FDFCF8',
    plate: 'transparent',
    surface: 'transparent',
    exact: true,
  },
};

export interface Paint {
  fill: string;
  stroke: string;
  strokeWidth: number;
  dash?: string | undefined;
  opacity?: number | undefined;
}

const INK = '#1B1B1F';
const SOFT = '#63676E';
const BRAND = '#7C3AED';
const BLUE = '#2E6BE6';

export function paintFor(hand: HandId, role: ShapeRole, active: boolean): Paint {
  const instrumentPaint = paintForInstrumentRole(role);
  if (instrumentPaint) return instrumentPaint;
  if (hand === 'soft-geometry') return softGeometryPaint(role, active);
  if (hand === 'editorial-contrast') return editorialContrastPaint(role, active);
  if (hand === 'living-line') return livingLinePaint(role, active);
  if (hand === 'technical-draft') {
    const stroke = active || role === 'positive' ? BRAND : role === 'pointer' || role === 'event' || role === 'series' || role === 'evidence' ? BLUE : role === 'conditional' || role === 'tail' ? '#C97700' : role === 'annotation' || role === 'grid' ? SOFT : INK;
    return {
      fill: role === 'free-space' || role === 'capacity' || role === 'conditional' || role === 'padding' || role === 'tail' ? 'url(#draft-hatch)' : '#FDFCF8',
      stroke,
      strokeWidth: active ? 1.8 : role === 'construction' ? 0.8 : role === 'dimension' ? 0.9 : 1.15,
      dash: role === 'construction' || role === 'capacity' || role === 'grid' || role === 'padding' ? '4 4' : undefined,
      opacity: role === 'construction' || role === 'grid' ? 0.66 : 1,
    };
  }

  if (hand === 'teacher-sketch') {
    const fill = active
      ? '#F0EAFD'
      : role === 'positive'
        ? '#F0EAFD'
        : role === 'negative'
          ? '#FFF5DF'
          : role === 'event' || role === 'series' || role === 'evidence'
            ? '#E9F0FE'
            : role === 'conditional' || role === 'tail' || role === 'padding'
              ? 'url(#sketch-hatch)'
      : role === 'pointer'
        ? '#E9F0FE'
        : role === 'free-space' || role === 'capacity'
          ? 'url(#sketch-hatch)'
          : role === 'header' || role === 'slot' || role === 'artifact'
            ? '#E8E5DC'
            : role === 'record'
              ? '#F8F5EC'
              : '#FDFCF8';
    return {
      fill,
      stroke: active || role === 'positive' ? BRAND : role === 'pointer' || role === 'event' || role === 'series' || role === 'evidence' ? BLUE : role === 'negative' || role === 'conditional' || role === 'tail' ? '#C97700' : role === 'annotation' || role === 'grid' ? SOFT : INK,
      strokeWidth: active ? 2.5 : role === 'construction' ? 1.25 : 1.9,
      dash: role === 'construction' || role === 'capacity' || role === 'grid' || role === 'padding' ? '7 6' : undefined,
      opacity: role === 'construction' || role === 'grid' ? 0.72 : 1,
    };
  }

  const fill = active
    ? '#F0EAFD'
    : role === 'positive'
      ? '#F0EAFD'
      : role === 'negative'
        ? '#FFF5DF'
        : role === 'event' || role === 'series' || role === 'evidence'
          ? '#E9F0FE'
          : role === 'conditional' || role === 'tail' || role === 'padding'
            ? 'url(#exact-hatch)'
    : role === 'pointer'
      ? '#E9F0FE'
      : role === 'capacity' || role === 'free-space'
        ? 'url(#exact-hatch)'
        : role === 'header' || role === 'artifact'
          ? '#E4E1D8'
          : role === 'slot'
            ? '#F0EAFD'
            : role === 'record'
              ? '#FDFCF8'
              : role === 'depth'
                ? '#DAD7CE'
                : '#F7F5EF';
  return {
    fill,
    stroke: active || role === 'positive' ? BRAND : role === 'pointer' || role === 'event' || role === 'series' || role === 'evidence' ? BLUE : role === 'negative' || role === 'conditional' || role === 'tail' ? '#C97700' : role === 'annotation' || role === 'grid' ? SOFT : INK,
    strokeWidth: active ? 2.4 : role === 'construction' ? 0.9 : role === 'dimension' ? 1.1 : 1.5,
    dash: role === 'construction' || role === 'capacity' || role === 'grid' || role === 'padding' ? '4 4' : undefined,
    opacity: role === 'construction' || role === 'grid' ? 0.62 : 1,
  };
}

function softGeometryPaint(role: ShapeRole, active: boolean): Paint {
  const violet = '#7C3AED';
  const blue = '#2E6BE6';
  const amber = '#C97700';
  const accent = active || role === 'active' || role === 'positive';
  const informative = role === 'pointer' || role === 'event' || role === 'series' || role === 'evidence';
  const caution = role === 'negative' || role === 'conditional' || role === 'tail';
  return {
    fill: accent ? '#E9DDFB' : informative ? '#DDE9FC' : caution ? '#FBE9C9' : role === 'surface' || role === 'record' || role === 'header' ? '#F2ECFB' : role === 'slot' ? '#EEF3FC' : 'transparent',
    stroke: accent ? violet : informative ? blue : caution ? amber : INK,
    strokeWidth: accent ? 2.1 : role === 'construction' || role === 'grid' ? 0.9 : 1.35,
    dash: role === 'construction' || role === 'grid' || role === 'capacity' ? '4 4' : undefined,
    opacity: role === 'construction' || role === 'grid' ? 0.58 : 1,
  };
}

function editorialContrastPaint(role: ShapeRole, active: boolean): Paint {
  const coral = '#D94F3D';
  const warm = '#D07A12';
  const filled = active || role === 'active' || role === 'positive';
  const causal = role === 'pointer' || role === 'event' || role === 'operator';
  return {
    fill: filled ? '#F6C8C1' : role === 'negative' ? '#E8E3DC' : role === 'header' ? '#F3D8D2' : role === 'surface' ? '#F5F0E9' : role === 'conditional' || role === 'tail' ? '#F8E7CF' : 'transparent',
    stroke: filled ? coral : causal ? warm : INK,
    strokeWidth: filled ? 2.6 : role === 'construction' || role === 'grid' ? 0.8 : causal ? 2.3 : 1.65,
    dash: role === 'construction' || role === 'grid' ? '3 5' : undefined,
    opacity: role === 'construction' || role === 'grid' ? 0.5 : 1,
  };
}

function livingLinePaint(role: ShapeRole, active: boolean): Paint {
  const green = '#2F855A';
  const purple = '#7651A8';
  const amber = '#C97700';
  const living = active || role === 'active' || role === 'positive' || role === 'record';
  const causal = role === 'pointer' || role === 'event' || role === 'operator';
  return {
    fill: living ? '#DCEFE2' : role === 'surface' || role === 'slot' ? '#EAF4EC' : role === 'header' || role === 'operator' ? '#EEE8F6' : role === 'conditional' || role === 'tail' ? '#FFF1D6' : 'transparent',
    stroke: living ? green : causal ? purple : role === 'negative' || role === 'conditional' || role === 'tail' ? amber : INK,
    strokeWidth: living || causal ? 2 : role === 'construction' || role === 'grid' ? 0.85 : 1.45,
    dash: role === 'construction' || role === 'grid' || role === 'capacity' ? '5 4' : undefined,
    opacity: role === 'construction' || role === 'grid' ? 0.55 : 1,
  };
}

/** Paint-time corner language. Bounds stay byte-identical; only the contour inside them changes. */
export function cornerRadiusFor(hand: HandId, shape: SceneShape): number | undefined {
  if (shape.kind !== 'rect') return undefined;
  if (hand === 'technical-draft' || hand === 'editorial-contrast') return 0;
  if (hand === 'soft-geometry') return Math.min(14, shape.box.height / 2, shape.box.width / 2);
  if (hand === 'living-line') return Math.min(18, shape.box.height / 2, shape.box.width / 2);
  return shape.radius ?? (hand === 'kiln-exact' ? 3 : 0);
}

export function strokeCapsFor(hand: HandId): { lineCap: 'butt' | 'round' | 'square'; lineJoin: 'miter' | 'round' | 'bevel' } {
  if (hand === 'technical-draft') return { lineCap: 'square', lineJoin: 'miter' };
  if (hand === 'editorial-contrast') return { lineCap: 'butt', lineJoin: 'miter' };
  return { lineCap: 'round', lineJoin: 'round' };
}

function paintForInstrumentRole(role: ShapeRole): Paint | undefined {
  const exact = (fill: string, stroke = fill): Paint => ({ fill, stroke, strokeWidth: 0.7 });
  if (role === 'ide-light-canvas') return exact('#FFFFFF', '#D7D7DC');
  if (role === 'ide-light-chrome') return exact('#F3F3F5', '#D7D7DC');
  if (role === 'ide-light-gutter') return exact('#F8F8FA', '#E5E5E8');
  if (role === 'ide-dark-canvas') return exact('#1E1E22', '#3A3940');
  if (role === 'ide-dark-chrome') return exact('#27262C', '#3A3940');
  if (role === 'ide-dark-gutter') return exact('#19191D', '#34333A');
  if (role === 'ide-light-selection') return exact('#F0EAFD', '#F0EAFD');
  if (role === 'ide-dark-selection') return exact('#302A40', '#302A40');
  if (role === 'ide-light-terminal') return exact('#FBFBFC', '#D7D7DC');
  if (role === 'ide-dark-terminal') return exact('#151519', '#3A3940');
  if (role === 'ide-status') return exact('#6D28D9', '#6D28D9');
  if (role === 'ide-run') return exact('#2E6BE6', '#2E6BE6');
  return undefined;
}

export function textColor(role: ShapeRole): string {
  if (role === 'ide-light-text') return '#202124';
  if (role === 'ide-light-muted') return '#70717A';
  if (role === 'ide-dark-text') return '#E7E7EA';
  if (role === 'ide-dark-muted') return '#92929C';
  if (role === 'ide-inverse-text') return '#FFFFFF';
  if (role === 'syntax-keyword') return '#6D28D9';
  if (role === 'syntax-literal') return '#8A5300';
  if (role === 'syntax-keyword-dark') return '#C4B5FD';
  if (role === 'syntax-literal-dark') return '#F0B35C';
  if (role === 'syntax-comment') return '#7A7B83';
  if (role === 'syntax-accent') return '#2E6BE6';
  if (role === 'terminal-output') return '#D9D9DE';
  if (role === 'terminal-success') return '#7FD1A8';
  return role === 'annotation' ? SOFT : INK;
}
